import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/supabaseClient';
import { Bell, Loader2, FileText, CheckCheck } from 'lucide-react';

/**
 * Header notifications bell. Polls /api/notifications every 60s.
 * Clicking a notification marks it as read. Clicking "Segna tutte come lette"
 * marks all unread as read.
 */
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await apiFetch('/api/notifications')); }
    catch (_) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const unread = items.filter((n) => !n.read_at).length;

  const markOne = async (id) => {
    try { await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' }); }
    catch (_) { /* silent */ }
    setItems((xs) => xs.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
  };
  const markAll = async () => {
    try { await apiFetch('/api/notifications/read-all', { method: 'POST' }); }
    catch (_) { /* silent */ }
    setItems((xs) => xs.map((n) => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
  };

  return (
    <div className="relative" ref={rootRef} data-testid="notifications-root">
      <button
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        aria-label="Notifiche"
        data-testid="notifications-bell"
        className="relative p-2 rounded-full bg-white border border-stone-200 text-stone-600 hover:text-brand-navy hover:border-stone-300 transition-colors"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-green text-white text-[10px] font-bold flex items-center justify-center border-2 border-app-background"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[92vw] rounded-2xl border border-stone-200 bg-white shadow-xl z-40" data-testid="notifications-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Notifiche</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-brand-blue hover:underline inline-flex items-center gap-1" data-testid="btn-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" /> Segna tutte lette
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="flex items-center justify-center py-6 text-stone-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="py-8 text-center text-sm text-stone-400" data-testid="notifications-empty">Nessuna notifica</div>
            )}
            {items.map((n, i) => (
              <button
                key={n.id}
                onClick={() => markOne(n.id)}
                data-testid={`notification-${i}`}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 flex gap-3 ${n.read_at ? '' : 'bg-emerald-50/40'}`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${n.read_at ? 'bg-stone-100 text-stone-400' : 'bg-brand-green/10 text-brand-green'}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate ${n.read_at ? 'text-stone-600' : 'text-brand-navy font-medium'}`}>{n.title}</div>
                  {n.body && <div className="text-xs text-stone-500 truncate mt-0.5">{n.body}</div>}
                  <div className="text-[11px] text-stone-400 mt-0.5">{new Date(n.created_at).toLocaleString('it-IT')}</div>
                </div>
                {!n.read_at && <span className="w-2 h-2 rounded-full bg-brand-green mt-2 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
